import React from 'react';
import MobileToolbar from '../toolbar/MobileToolbar';
import ChatSidebar from '../chat/ChatSidebar';
import SettingsOverlay from '../settings/SettingsOverlay';
import DualChatLayout from '../DualChatLayout';
import ChatWindow from '../chat/ChatWindow';
import ArtifactRenderer from '../ArtifactRenderer';
import MessageInput from '../input/MessageInput';

const MobileResizeHandle = ({ chatWidth, artifactWidth, onResize }) => (
  <div
    className="absolute left-0 top-0 bottom-0 w-2 bg-gray-300 dark:bg-gray-600 cursor-col-resize transition-all duration-150 z-10 group"
    onTouchStart={(e) => {
      e.preventDefault();
      const startX = e.touches[0].clientX;
      const startChatWidth = chatWidth;
      const containerWidth = window.innerWidth;
      
      // Get elements for direct DOM manipulation
      const chatElement = e.target.parentElement.parentElement.querySelector('[data-chat-section]');
      const artifactElement = e.target.closest('[data-artifact-section]');
      
      // Disable transitions during drag
      if (chatElement) chatElement.style.transition = 'none';
      if (artifactElement) artifactElement.style.transition = 'none';
      
      let lastUpdateTime = 0;
      const UPDATE_INTERVAL = 16; // ~60fps

      const handleTouchMove = (e) => {
        const now = Date.now();
        if (now - lastUpdateTime < UPDATE_INTERVAL) return;
        lastUpdateTime = now;
        
        const deltaX = e.touches[0].clientX - startX;
        const deltaPercent = (deltaX / containerWidth) * 100;
        
        const newChatWidth = Math.max(30, Math.min(80, startChatWidth + deltaPercent));
        const newArtifactWidth = 100 - newChatWidth;
        
        // Update DOM directly
        if (chatElement) {
          chatElement.style.width = `${newChatWidth}%`;
        }
        if (artifactElement) {
          artifactElement.style.width = `${newArtifactWidth}%`;
        }
      };

      const handleTouchEnd = (e) => {
        // Calculate final dimensions
        const deltaX = e.changedTouches[0].clientX - startX;
        const deltaPercent = (deltaX / containerWidth) * 100;
        const finalChatWidth = Math.max(30, Math.min(80, startChatWidth + deltaPercent));
        const finalArtifactWidth = 100 - finalChatWidth;
        
        // Re-enable transitions
        if (chatElement) chatElement.style.transition = '';
        if (artifactElement) artifactElement.style.transition = '';
        
        // Update React state once at the end
        onResize(finalChatWidth, finalArtifactWidth);
        
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);
      };

      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleTouchEnd);
    }}
  >
    {/* Mobile resize handle indicator */}
    <div className="absolute left-1/4 top-1/2 transform -translate-x-1/2 -translate-y-1/2 w-1 h-8 bg-gray-400 dark:bg-gray-500 rounded-full opacity-60 group-hover:opacity-100 group-hover:scale-125 transition-all duration-150"></div>
  </div>
);

const MobileLayout = ({
  // Chat state
  selectedChat,
  messages,
  secondaryMessages,
  selectedModel,
  selectedModelName,
  secondaryModel,
  isDualMode,
  reasoningStream,
  inputText,
  showSettings,
  chatTitle,
  messageLimit,
  isLoadingMessages,
  editingMessage,
  
  // Subscription state
  hasSubscription,
  hasBasicSubscription,
  subscriptionSources,
  hasMaxSubscription,
  subscriptionStatus,
  isCheckingSubscription,
  onRefreshSubscription,
  
  // UI state
  isSidebarOpen,
  chatWidth,
  artifactWidth,
  currentArtifact,
  isArtifactExpanded,
  particlesEnabled,
  setParticlesEnabled,
  
  // User data
  userName,
  userEmail,
  chats,
  projects,
  activeProjectId,
  isLoadingProjects,
  projectError,
  chatSidebarViewMode,
  setChatSidebarViewMode,
  
  // Handlers
  toggleSidebar,
  onNewChat,
  onChatSelect,
  onProjectSelect,
  onCreateProject,
  onUpdateProject,
  onDeleteProject,
  onMoveChatToProject,
  onUpgrade,
  onSettingsClick,
  onBackToChat,
  onLogout,
  onQuestionClick,
  onArtifactOpen,
  onArtifactClose,
  onToggleArtifactExpanded,
  onResize,
  onModelChange,
  onPinnedModelChange,
  onMessageSent,
  onSecondaryMessageSent,
  updateChatTitle,
  onReasoningStreamUpdate,
  setInputText,
  onStartEditMessage,
  onCancelEdit,
  applyEditAndTruncate,
  realtimeVoice
}) => {
  const currentMessages = messages[selectedChat?.id] || [];
  const isLoadingExistingChat = Boolean(selectedChat?.id && isLoadingMessages);
  const isEmptyChat = currentMessages.length === 0 && !isLoadingExistingChat;

  return (
    <div className="flex theme-app-bg transition-colors duration-200">
      <div className="flex flex-col h-full w-full">
        {/* Fixed MobileToolbar at the top */}
        <div className="fixed top-0 left-0 right-0 z-50">
          <MobileToolbar
            toggleSidebar={toggleSidebar}
            onNewChat={onNewChat}
            chatTitle={chatTitle}
            model={selectedChat ? selectedChat.model : selectedModel}
            selectedModel={selectedModel}
          />
        </div>

        {/* ChatWindow between toolbar and input */}
        <div className="flex-grow overflow-hidden pt-16 pb-20">
          <div className="h-full overflow-y-auto">
            {currentArtifact && isArtifactExpanded ? (
              <ArtifactRenderer
                artifact={currentArtifact}
                onClose={onArtifactClose}
                isExpanded={isArtifactExpanded}
                onToggleExpanded={onToggleArtifactExpanded}
              />
            ) : (
              <div className="flex h-full">
                <div 
                  className="transition-all duration-300"
                  style={{ width: currentArtifact ? `${chatWidth}%` : '100%' }}
                  data-chat-section
                >
                  {(isEmptyChat || isLoadingExistingChat) && !isDualMode ? (
                    <div className="flex flex-col items-center justify-center h-full space-y-3 text-center px-6">
                      <div className="flex items-center space-x-3">
                        <div className="h-6 w-6 border-2 border-gray-200 dark:border-gray-700 border-t-indigo-500 dark:border-t-indigo-400 rounded-full animate-spin" aria-hidden="true" />
                        <p className="text-base font-medium text-gray-800 dark:text-gray-100">Loading messages…</p>
                      </div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Fetching your previous conversation.</p>
                    </div>
                  ) : isDualMode ? (
                    <DualChatLayout
                      selectedChat={selectedChat}
                      primaryMessages={messages[selectedChat?.id] || []}
                      secondaryMessages={secondaryMessages[selectedChat?.id] || []}
                      primaryModel={selectedModel}
                      secondaryModel={secondaryModel || selectedModel}
                      reasoningStream={reasoningStream}
                      onQuestionClick={onQuestionClick}
                      onArtifactOpen={onArtifactOpen}
                      onEditMessage={onStartEditMessage ? (msg) => onStartEditMessage(selectedChat?.id, msg) : undefined}
                      onMessageSent={onMessageSent}
                      updateChatTitle={updateChatTitle}
                      hasSubscription={hasSubscription}
                      messageLimit={messageLimit}
                      editingMessage={editingMessage}
                      onCancelEdit={onCancelEdit}
                      applyEditAndTruncate={applyEditAndTruncate}
                    />
                  ) : (
                    <ChatWindow
                      selectedChat={selectedChat}
                      messages={messages[selectedChat?.id] || []}
                      selectedModel={selectedModel}
                      isLoadingMessages={isLoadingMessages}
                      reasoningStream={reasoningStream}
                      onQuestionClick={onQuestionClick}
                      onArtifactOpen={onArtifactOpen}
                      onEditMessage={onStartEditMessage ? (msg) => onStartEditMessage(selectedChat?.id, msg) : undefined}
                      onMessageSent={onMessageSent}
                      updateChatTitle={updateChatTitle}
                      hasSubscription={hasSubscription}
                      messageLimit={messageLimit}
                      editingMessage={editingMessage}
                      onCancelEdit={onCancelEdit}
                      applyEditAndTruncate={applyEditAndTruncate}
                    />
                  )}
                </div>
                {currentArtifact && !isArtifactExpanded && (
                  <div 
                    className="flex-shrink-0 h-full transition-all duration-300 relative"
                    style={{ width: `${artifactWidth}%` }}
                    data-artifact-section
                  >
                    <MobileResizeHandle
                      chatWidth={chatWidth}
                      artifactWidth={artifactWidth}
                      onResize={onResize}
                    />
                    <ArtifactRenderer
                      artifact={currentArtifact}
                      onClose={onArtifactClose}
                      isExpanded={isArtifactExpanded}
                      onToggleExpanded={onToggleArtifactExpanded}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Fixed MessageInput at the bottom */}
        <div className="fixed bottom-0 left-0 right-0 theme-app-bg z-40">
          <MessageInput
            selectedChat={selectedChat}
            activeProjectId={activeProjectId}
            projects={projects}
            onProjectSelect={onProjectSelect}
            selectedModel={selectedModel}
            selectedModelName={selectedChat ? selectedChat.model : selectedModelName}
            onModelChange={onModelChange}
            onPinnedModelChange={onPinnedModelChange}
            onMessageSent={onMessageSent}
            messages={messages[selectedChat?.id] || []}
            updateChatTitle={updateChatTitle}
            hasSubscription={hasSubscription}
            hasBasicSubscription={hasBasicSubscription}
            onUpgrade={onUpgrade}
            messageLimit={messageLimit}
            disableInput={
              (selectedChat?.id && (messages[selectedChat.id]?.length / 2 ?? 0) > 30) ||
              selectedModel === 'DeprecatedModel'
            }
            onReasoningStreamUpdate={onReasoningStreamUpdate}
            inputText={inputText}
            setInputText={setInputText}
            isDualMode={isDualMode}
            secondaryModel={secondaryModel}
            onSecondaryMessageSent={onSecondaryMessageSent}
            secondaryMessages={secondaryMessages[selectedChat?.id] || []}
            editingMessage={editingMessage}
            onCancelEdit={onCancelEdit}
            applyEditAndTruncate={applyEditAndTruncate}
            realtimeVoice={realtimeVoice}
          />
        </div>

        {/* Overlay + Sidebar when open */}
        {isSidebarOpen && (
          <>
            <div
              className="fixed inset-0 bg-black bg-opacity-50 z-30"
              onClick={toggleSidebar}
            ></div>
            <div className="fixed inset-y-0 left-0 w-[320px] z-40 mt-16">
              <ChatSidebar
                isOpen={isSidebarOpen}
                chats={chats}
                projects={projects}
                activeProjectId={activeProjectId}
                isLoadingProjects={isLoadingProjects}
                projectError={projectError}
                onChatSelect={onChatSelect}
                onProjectSelect={onProjectSelect}
                onCreateProject={onCreateProject}
                onUpdateProject={onUpdateProject}
                onDeleteProject={onDeleteProject}
                onMoveChatToProject={onMoveChatToProject}
                onNewChat={onNewChat}
                selectedChatId={selectedChat?.id}
                hasSubscription={hasSubscription}
                hasBasicSubscription={hasBasicSubscription}
                onUpgrade={onUpgrade}
                userName={userName}
                userEmail={userEmail}
                onSettingsClick={onSettingsClick}
                isMobile={true}
                viewMode={chatSidebarViewMode}
                messagesByChatId={messages}
              />
            </div>
          </>
        )}

        <SettingsOverlay
          isOpen={showSettings}
          onClose={onBackToChat}
          hasSubscription={hasSubscription}
          hasBasicSubscription={hasBasicSubscription}
          subscriptionSources={subscriptionSources}
          hasMaxSubscription={hasMaxSubscription}
          subscriptionStatus={subscriptionStatus}
          isCheckingSubscription={isCheckingSubscription}
          onRefreshSubscription={onRefreshSubscription}
          onLogout={onLogout}
          onUpgrade={onUpgrade}
          particlesEnabled={particlesEnabled}
          setParticlesEnabled={setParticlesEnabled}
          chatSidebarViewMode={chatSidebarViewMode}
          setChatSidebarViewMode={setChatSidebarViewMode}
        />
      </div>
    </div>
  );
};

export default MobileLayout;
